import {useEffect, useRef, useState} from "react";
import axios from "axios";
import { HubConnection, HubConnectionBuilder, JsonHubProtocol, LogLevel } from "@microsoft/signalr";
import config from "../config";


export interface SignalRConnection {
    connection: HubConnection | null;
    start: () => Promise<void>;
    stop: () => Promise<void>;
    on: (method: string, callback: (...args: any[]) => void) => void;
    off: (method: string, callback?: (...args: any[]) => void) => void;
    isConnected: boolean;
    error: Error | null;
}

export function useSignalRConnection(hubName: string): SignalRConnection {
    const connectionRef = useRef<HubConnection | null>(null);
    const [isConnected, setConnected] = useState(false);
    const [error, setError] = useState<Error | null>(null);

    async function negotiate() {
        const token = localStorage.getItem("coreToken");

        if (!token) {
            throw new Error("No authentication token found");
        }

        const response = await axios.post(
            `${config.coreApiUrl}signalr/${hubName}/negotiate`,
            null,
            { headers: { Authorization: `Bearer ${token}` } }
        );

        return response.data;
    }

    async function buildConnection() {
        const { url, accessToken } = await negotiate();

        const connection = new HubConnectionBuilder()
            .withUrl(url, {
                accessTokenFactory: () => accessToken
            })
            .withHubProtocol(new JsonHubProtocol())
            .withAutomaticReconnect()
            .configureLogging(LogLevel.Information)
            .build();

        connection.onclose(() => setConnected(false));
        connection.onreconnecting(() => setConnected(false));
        connection.onreconnected(() => setConnected(true));

        connectionRef.current = connection;
    }

    async function start() {
        try{
            setError(null);

            if (!connectionRef.current) {
                await buildConnection();
            }

            if (connectionRef.current!.state === "Connected") return;

            await connectionRef.current!.start();
            setConnected(true);
        }
        catch (error) {
            const err = error instanceof Error ? error : new Error(String(error));
            setError(err);
            setConnected(false);
            throw err;
        }
    }

    async function stop() {
        try {
            if (connectionRef.current && connectionRef.current.state !== "Disconnected") {
                await connectionRef.current.stop();
                setConnected(false);
            }
        } catch (err) {
            const error = err instanceof Error ? err : new Error(String(err));
            setError(error);
            throw error;
        }
    }

    function on(method: string, callback: (...args: any[]) => void) {
        if (!connectionRef.current) {
            console.warn(`Cannot subscribe to '${method}': connection not initialized`);
            return;
        }
        connectionRef.current.on(method, callback);
    }

    function off(method: string, callback?: (...args: any[]) => void) {
        if (callback) {
            connectionRef.current?.off(method, callback);
        } else {
            connectionRef.current?.off(method);
        }
    }

    useEffect(() => {
        return () => {
            void stop();
        };
    }, []);

    return {
        connection: connectionRef.current,
        start,
        stop,
        on,
        off,
        isConnected,
        error
    };
}